import { Suspense } from 'react';
import MoreVert from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useV3ExperienceState } from 'hooks/useV3ExperienceState';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { V3CookieService } from 'services/cookie/v3CookieService';
import { CINDER_URLS } from 'utils/cinder-urls';
import goToCinder from 'utils/goToCinder';
import { SaveDocumentAsTemplateDialog } from './SaveDocumentAsTemplate/SaveDocumentAsTemplateDialog';
import { alpha } from '@mui/material/styles';
import Divider from '@mui/material/Divider';

type MoreActionsButtonProps = {
  contentId?: string;
  contentName?: string;
};

const MoreActionsButtonInner = ({ contentId, contentName }: MoreActionsButtonProps) => {
  const { t } = useTranslation();
  const { isUsingV3Beta } = useV3ExperienceState();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);

  if (!contentId && !isUsingV3Beta) return null;

  const handleSaveAsTemplateClick = () => {
    setMenuOpen(false);
    setDialogOpen(true);
  };

  const handleSwitchToLegacyClick = () => {
    setMenuOpen(false);
    V3CookieService.clearV3Experience();
    goToCinder(CINDER_URLS.dashboard);
  };

  return (
    <>
      <IconButton
        ref={anchorEl}
        onClick={() => setMenuOpen(true)}
        data-testid="more-actions-button"
        sx={{
          backgroundColor: (theme) => (menuOpen ? alpha(theme.palette.primary.main, 0.3) : undefined),
        }}
      >
        <MoreVert color="primary" />
      </IconButton>
      <Menu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorEl={anchorEl.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock={true}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          },
        }}
      >
        {contentId && (
          <MenuItem onClick={handleSaveAsTemplateClick} sx={{ width: 291 }}>
            <Typography variant="body1" color="primary">
              {t('library.save_as_template_menu_item')}
            </Typography>
          </MenuItem>
        )}
        {contentId && isUsingV3Beta && (
          <Divider
            sx={{
              borderColor: (theme) => alpha(theme.palette.common.black, 0.12),
            }}
          />
        )}
        {isUsingV3Beta && (
          <MenuItem onClick={handleSwitchToLegacyClick} sx={{ width: 291 }}>
            <Typography variant="body1" color="primary">
              Switch to Legacy Proposify
            </Typography>
          </MenuItem>
        )}
      </Menu>
      {contentId && contentName && (
        <SaveDocumentAsTemplateDialog
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          contentId={contentId}
          contentName={contentName}
        />
      )}
    </>
  );
};

export const MoreActionsButton = (props: MoreActionsButtonProps) => {
  return (
    <Suspense fallback={null}>
      <MoreActionsButtonInner {...props} />
    </Suspense>
  );
};
